import React, { createContext, useContext, useEffect, useMemo, useReducer, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  SidebarContextType, 
  SidebarState, 
  SidebarActions, 
  SidebarDesign 
} from '@/types/sidebar';
import { useScreenSize } from '@/hooks/useScreenSize';

/**
 * Sidebar Context Provider
 * 
 * Manages sidebar state following Dieter Rams principles:
 * - Unobtrusive: Sidebar state doesn't interfere with app flow
 * - Useful: Provides all necessary state and actions
 * - Understandable: Clear state management patterns
 */

// Sidebar state actions
type SidebarAction = 
  | { type: 'TOGGLE' }
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'COLLAPSE' }
  | { type: 'EXPAND' }
  | { type: 'TOGGLE_COLLAPSE' }
  | { type: 'SET_SCREEN_SIZE'; payload: 'mobile' | 'tablet' | 'desktop' }
  | { type: 'SET_MOBILE'; payload: boolean };

// Initial state
const initialState: SidebarState = {
  isOpen: false,
  isCollapsed: false,
  isMobile: false,
  screenSize: 'desktop'
};

// State reducer
const sidebarReducer = (state: SidebarState, action: SidebarAction): SidebarState => {
  switch (action.type) {
    case 'TOGGLE':
      return {
        ...state,
        isOpen: !state.isOpen
      };
    
    case 'OPEN':
      return {
        ...state,
        isOpen: true
      };
    
    case 'CLOSE':
      return {
        ...state,
        isOpen: false
      };
    
    case 'COLLAPSE':
      return {
        ...state,
        isCollapsed: true
      };
    
    case 'EXPAND':
      return {
        ...state,
        isCollapsed: false
      };
    
    case 'TOGGLE_COLLAPSE':
      return {
        ...state,
        isCollapsed: !state.isCollapsed
      };
    
    case 'SET_SCREEN_SIZE': {
      const isMobile = action.payload === 'mobile';
      const isDesktop = action.payload === 'desktop';
      return {
        ...state,
        screenSize: action.payload,
        isMobile,
        // Auto-close on mobile when screen size changes
        isOpen: isMobile ? false : state.isOpen,
        // Collapsed (icons only) on mobile and tablet, expanded on desktop
        isCollapsed: !isDesktop
      };
    }
    
    case 'SET_MOBILE':
      return {
        ...state,
        isMobile: action.payload,
        isOpen: action.payload ? false : state.isOpen
      };
    
    default:
      return state;
  }
};

// Default sidebar configuration following Dieter Rams principles.
// Layout/behavior defaults only — navigation content (sections, user menu,
// branding) must come from the caller's config (see src/config/navigation.ts).
// A missing navigation merges as empty, never as a baked-in nav with links
// that may not exist.
const defaultSidebarConfig: Omit<SidebarDesign, 'navigation'> = {
  layout: {
    mobile: 'overlay',
    desktop: 'collapsible',
    breakpoints: {
      mobile: '640px',
      tablet: '768px',
      desktop: '1024px'
    },
    defaultCollapsed: false,
    collapsedWidth: '4.5rem',
    expandedWidth: '12rem'
  },
  components: {
    shadcnComponents: ['Sheet', 'Avatar', 'Separator', 'ScrollArea', 'Button'],
    customComponents: ['Sidebar', 'NavigationMenu', 'UserMenu', 'MobileMenuOverlay']
  },
  accessibility: {
    announceNavigation: true,
    skipLinks: true,
    focusManagement: true,
    keyboardNavigation: true,
    screenReaderSupport: true
  },
  responsiveStates: [
    {
      breakpoint: 'mobile',
      behavior: 'overlay',
      showLabels: true,
      showSections: true
    },
    {
      breakpoint: 'tablet', 
      behavior: 'collapsed',
      showLabels: false,
      showSections: false
    },
    {
      breakpoint: 'desktop',
      behavior: 'expanded',
      showLabels: true,
      showSections: true
    }
  ],
  theme: {
    variant: 'default',
    colorScheme: 'system',
    borderRadius: 'md'
  }
};

// Create context
const SidebarContext = createContext<SidebarContextType | null>(null);

// Provider props
interface SidebarProviderProps {
  children: ReactNode;
  config?: Partial<SidebarDesign>;
}

// Provider component
export const SidebarProvider: React.FC<SidebarProviderProps> = ({ 
  children, 
  config: customConfig 
}) => {
  const [state, dispatch] = useReducer(sidebarReducer, initialState);
  const location = useLocation();
  
  // Use consolidated screen size detection
  const { screenSize } = useScreenSize();
  
  // Merge custom config with defaults (memoized — identity-stable across renders)
  const config: SidebarDesign = useMemo(() => ({
    ...defaultSidebarConfig,
    ...customConfig,
    layout: {
      ...defaultSidebarConfig.layout,
      ...customConfig?.layout
    },
    navigation: {
      sections: [],
      userMenu: { trigger: 'avatar', items: [] },
      branding: { title: '', href: '/' },
      ...customConfig?.navigation
    }
  }), [customConfig]);

  // Update state when screen size changes
  useEffect(() => {
    dispatch({ type: 'SET_SCREEN_SIZE', payload: screenSize });
  }, [screenSize]);

  // Close mobile menu on navigation
  useEffect(() => {
    if (state.isMobile && state.isOpen) {
      dispatch({ type: 'CLOSE' });
    }
  }, [location.pathname, state.isMobile, state.isOpen]);

  // Set initial collapsed state based on config
  useEffect(() => {
    if (config.layout.defaultCollapsed && !state.isMobile) {
      dispatch({ type: 'COLLAPSE' });
    }
  }, [config.layout.defaultCollapsed, state.isMobile]);

  // Actions (memoized — dispatch is stable)
  const actions: SidebarActions = useMemo(() => ({
    toggle: () => dispatch({ type: 'TOGGLE' }),
    open: () => dispatch({ type: 'OPEN' }),
    close: () => dispatch({ type: 'CLOSE' }),
    collapse: () => dispatch({ type: 'COLLAPSE' }),
    expand: () => dispatch({ type: 'EXPAND' }),
    setScreenSize: (size: 'mobile' | 'tablet' | 'desktop') =>
      dispatch({ type: 'SET_SCREEN_SIZE', payload: size })
  }), []);

  // Current sidebar state available in context

  // Force collapse on the new-site wizard so the Puck editor has more room.
  const forceCollapsed = location.pathname === '/sites/new';

  // Context value (memoized — identity-stable across renders)
  const contextValue: SidebarContextType = useMemo(() => ({
    // State
    isOpen: state.isOpen,
    isCollapsed: forceCollapsed || state.isCollapsed,
    isMobile: state.isMobile,
    currentPath: location.pathname,

    // Actions
    toggle: actions.toggle,
    open: actions.open,
    close: actions.close,
    toggleCollapse: () => dispatch({ type: 'TOGGLE_COLLAPSE' }),

    // Configuration
    config
  }), [
    state.isOpen,
    state.isCollapsed,
    state.isMobile,
    forceCollapsed,
    location.pathname,
    actions,
    config
  ]);

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};

// Hook to use sidebar context
export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  
  return context;
};

export default SidebarProvider;